import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import { useAWSAuth as useAuth } from '../contexts/AWSAuthContext';
import { useSubscription } from '../hooks/useSubscription';
import { Button } from './ui/Button';
import { Home, Camera, Leaf, Apple, TrendingUp, DollarSign, User, Menu, X, LogIn, UserPlus, Sparkles, Search, MessageCircle, Bot, Stethoscope } from 'lucide-react';
import { useToast } from './ui/Toast';

const Nav = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  padding-top: env(safe-area-inset-top, 0px);
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid ${theme.colors.border.light};
  z-index: 1000;
  box-shadow: ${theme.shadows.sm};
  max-width: 100vw;
  overflow-x: hidden;
  display: flex;
  justify-content: flex-start;
`;

const NavContainer = styled.div<{ $tier: 'plus' | 'pro' | 'elite' }>`
  max-width: ${props => props.$tier === 'elite' ? 'calc(100vw - 280px)' : '98vw'};
  margin: ${props => props.$tier === 'elite' ? '0' : '0 auto'};
  margin-left: ${props => props.$tier === 'elite' ? '0' : 'auto'};
  padding: ${props => props.$tier === 'elite' ? '0 0 0 0.75rem' : '0 1rem'};
  display: flex;
  align-items: center;
  justify-content: ${props => props.$tier === 'elite' ? 'flex-start' : 'space-between'};
  gap: 0;
  height: 72px;
  flex-wrap: nowrap;
  position: relative;
  overflow-x: visible;
  overflow-y: visible;
  width: ${props => props.$tier === 'elite' ? '100%' : '100%'};

  @media (min-width: 1024px) {
    padding: ${props => props.$tier === 'elite' ? '0 0 0 0.75rem' : '0 1.25rem'};
  }

  @media (min-width: 1280px) {
    padding: ${props => props.$tier === 'elite' ? '0 0 0 0.75rem' : '0 1.5rem'};
    max-width: ${props => props.$tier === 'elite' ? 'calc(100vw - 280px)' : '1600px'};
  }

  @media (min-width: 1440px) {
    max-width: ${props => props.$tier === 'elite' ? 'calc(100vw - 280px)' : '1600px'};
  }

  @media (max-width: 1023px) {
    max-width: 100vw;
    padding: 0 0.5rem;
    overflow-x: hidden;
    width: 100%;
    justify-content: flex-start;
    gap: 0;
  }
`;

const Logo = styled(Link)<{ $tier?: 'plus' | 'pro' | 'elite' }>`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: ${props => props.$tier === 'elite'
    ? `calc(${theme.typography.fontSize['2xl']} * 1.1025 * 1.3)`
    : `calc(${theme.typography.fontSize['2xl']} * 1.1025)`};
  font-weight: ${theme.typography.fontWeight.bold};
  background: linear-gradient(135deg, ${theme.colors.primary[600]} 0%, ${theme.colors.mint[500]} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: transform 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;
  position: ${props => (props.$tier === 'plus' || props.$tier === 'pro') ? 'absolute' : 'relative'};
  left: ${props => (props.$tier === 'plus' || props.$tier === 'pro') ? '1rem' : 'auto'};
  z-index: 1;
  margin: 0;
  padding: 0;
  transform: translateX(5%);

  &:hover {
    transform: translateX(5%) scale(1.05);
  }

  @media (min-width: 1024px) {
    left: ${props => (props.$tier === 'plus' || props.$tier === 'pro') ? '1.25rem' : 'auto'};
  }

  @media (min-width: 1280px) {
    left: ${props => (props.$tier === 'plus' || props.$tier === 'pro') ? '1.5rem' : 'auto'};
  }

  @media (max-width: ${theme.breakpoints.md}) {
    font-size: ${props => props.$tier === 'elite'
      ? `calc(${theme.typography.fontSize.xl} * 1.1025 * 1.3)`
      : `calc(${theme.typography.fontSize.xl} * 1.1025)`};
    position: relative;
    left: auto;
  }
`;


const LeftGroup = styled.div<{ $tier: 'plus' | 'pro' | 'elite' }>`
  display: flex;
  align-items: center;
  gap: 0.875rem;
  flex: ${props => props.$tier === 'elite' ? '1 1 0' : '0 0 auto'};
  flex-shrink: ${props => props.$tier === 'elite' ? '1' : '0'};
  min-width: 0;
  max-width: ${props => props.$tier === 'elite' ? 'calc(100% - 240px)' : 'none'};
  white-space: nowrap;
  overflow: visible;

  @media (max-width: 1279px) {
    gap: 0.75rem;
    max-width: ${props => props.$tier === 'elite' ? 'calc(100% - 220px)' : 'none'};
  }

  @media (max-width: 1023px) {
  flex: 1 1 auto;
    gap: 12px;
    max-width: none;
    overflow: visible;
  }
`;

/* Wrapper for horizontal scroll on mobile - keeps all nav tabs visible like desktop */
const NavScrollArea = styled.div<{ $tier: 'plus' | 'pro' | 'elite' }>`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  overflow: visible;

  @media (max-width: 1023px) {
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
    flex-wrap: nowrap;
    &::-webkit-scrollbar {
      height: 4px;
    }
  }
`;

const NavLinksContainer = styled.nav<{ $tier: 'plus' | 'pro' | 'elite'; $isHomePageNotSignedIn?: boolean; $isAuthPageNotSignedIn?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${props => props.$tier === 'elite' ? '0.35rem' : '0.3rem'};
  flex-shrink: 1;
  min-width: 0;
  max-width: none;
  flex-wrap: nowrap;
  white-space: nowrap;
  overflow: visible;
  justify-content: ${props => (props.$tier === 'plus' || props.$tier === 'pro') ? 'center' : 'flex-start'};
  margin-left: ${props => props.$tier === 'elite' ? '0.75rem' : '0'};
  margin-right: ${props => props.$tier === 'elite' ? '0.5rem' : '0'};
  position: ${props => (props.$tier === 'plus' || props.$tier === 'pro') ? 'absolute' : 'relative'};
  left: ${props => (props.$tier === 'plus' || props.$tier === 'pro') ? 'calc(50% - 60px + 10% - 5%)' : 'auto'};
  transform: ${props => {
    if (props.$isHomePageNotSignedIn || props.$isAuthPageNotSignedIn) {
      if (props.$tier === 'plus' || props.$tier === 'pro') {
        return 'translateX(calc(-50% - 9% - 1vw))';
      }
      if (props.$tier === 'elite') {
        return 'translateX(calc(3% - 9% - 1vw))';
      }
      return 'translateX(calc(-9% - 1vw))';
    }
    if (props.$tier === 'plus' || props.$tier === 'pro') return 'translateX(calc(-50% - 1vw))';
    if (props.$tier === 'elite') return 'translateX(calc(3% - 1vw))';
    return 'none';
  }};
  flex: ${props => (props.$tier === 'plus' || props.$tier === 'pro') ? '0 0 auto' : '0 1 auto'};
  padding-right: ${props => props.$tier === 'elite' ? '1.25rem' : '0'};

  @media (max-width: 1279px) {
    gap: ${props => props.$tier === 'elite' ? '0.3rem' : '0.25rem'};
    margin-left: ${props => props.$tier === 'elite' ? '0.625rem' : '0'};
    margin-right: ${props => props.$tier === 'elite' ? '0.5rem' : '0'};
    padding-right: ${props => props.$tier === 'elite' ? '1rem' : '0'};
    left: ${props => (props.$tier === 'plus' || props.$tier === 'pro') ? 'calc(50% - 50px + 10% - 5%)' : 'auto'};
    transform: ${props => {
      if (props.$isHomePageNotSignedIn || props.$isAuthPageNotSignedIn) {
        if (props.$tier === 'plus' || props.$tier === 'pro') {
          return 'translateX(calc(-50% - 9%))';
        }
        if (props.$tier === 'elite') {
          return 'translateX(calc(3% - 9% - 1vw))';
        }
        return 'translateX(calc(-9% - 1vw))';
      }
      if (props.$tier === 'plus' || props.$tier === 'pro') return 'translateX(calc(-50% - 1vw))';
      if (props.$tier === 'elite') return 'translateX(calc(3% - 1vw))';
      return 'none';
    }};
  }

  /* Mobile: keep visible, use horizontal scroll - same tabs as desktop */
  @media (max-width: 1023px) {
    position: relative;
    left: auto;
    transform: none;
    flex-shrink: 0;
    margin-left: 0.5rem;
    margin-right: 0.5rem;
    padding-right: 0.5rem;
  }
`;

const NavLinksContainerElite = styled.nav`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  flex-shrink: 1;
  min-width: 0;
  max-width: none;
  flex-wrap: nowrap;
  white-space: nowrap;
  overflow: visible;
  justify-content: flex-start;
  margin-left: 0.75rem;
  margin-right: 1.5rem;
  position: relative;
  flex: 1 1 auto;

  @media (max-width: 1280px) {
    gap: 0.5rem;
    margin-left: 0.5rem;
    margin-right: 1.25rem;
  }
`;

// Elite-specific groups
const LeftGroupElite = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1 1 auto;
  overflow: visible;
  min-width: 0;
  justify-content: flex-start;

  @media (max-width: 1280px) {
    gap: 0.75rem;
  }
`;

const CenterGroupElite = styled.div`
  display: flex;
  justify-content: center;
  flex: 0 0 auto;
  flex-shrink: 0;
  margin-left: 1rem;
  margin-right: 1rem;

  @media (max-width: 1280px) {
    margin-left: 0.75rem;
    margin-right: 0.75rem;
  }
`;

const RightGroupElite = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 0 0 auto;
  flex-shrink: 0;
  margin-left: 1rem;

  @media (max-width: 1280px) {
    gap: 0.625rem;
    margin-left: 0.75rem;
  }
`;

const RightGroup = styled.div<{ $tier: 'plus' | 'pro' | 'elite' }>`
  display: flex;
  align-items: center;
  gap: ${props => props.$tier === 'elite' ? '0.75rem' : '0.75rem'};
  flex-shrink: 0;
  flex-grow: 0;
  min-width: fit-content;
  position: ${props => {
    if (props.$tier === 'elite') return 'fixed';
    if (props.$tier === 'plus' || props.$tier === 'pro') return 'absolute';
    return 'relative';
  }};
  right: ${props => {
    if (props.$tier === 'elite') return '1rem';
    if (props.$tier === 'plus' || props.$tier === 'pro') return '1rem';
    return 'auto';
  }};
  top: ${props => (props.$tier === 'elite' || props.$tier === 'plus' || props.$tier === 'pro') ? '0' : 'auto'};
  z-index: 1001;
  flex-wrap: nowrap;
  white-space: nowrap;
  margin-left: ${props => props.$tier === 'elite' ? '0' : 'auto'};
  overflow: visible;
  padding-right: 0;
  margin-right: 0;
  justify-content: flex-start;
  height: ${props => (props.$tier === 'elite' || props.$tier === 'plus' || props.$tier === 'pro') ? '72px' : 'auto'};
  align-items: center;

  @media (min-width: 1024px) {
    right: ${props => {
      if (props.$tier === 'elite') return '1.25rem';
      if (props.$tier === 'plus' || props.$tier === 'pro') return '1.25rem';
      return 'auto';
    }};
  }

  @media (min-width: 1280px) {
    right: ${props => {
      if (props.$tier === 'elite') return '1.5rem';
      if (props.$tier === 'plus' || props.$tier === 'pro') return '1.5rem';
      return 'auto';
    }};
  }

  @media (max-width: 1279px) {
    gap: ${props => props.$tier === 'elite' ? '0.625rem' : '0.625rem'};
    margin-left: ${props => props.$tier === 'elite' ? '0' : 'auto'};
    right: ${props => {
      if (props.$tier === 'elite') return '1rem';
      if (props.$tier === 'plus' || props.$tier === 'pro') return '1rem';
      return 'auto';
    }};
  }

  @media (max-width: 1023px) {
    gap: 0.5rem;
    padding-right: 0.5rem;
    margin-left: 0.5rem;
    position: relative;
    right: auto;
    top: auto;
    height: auto;
    flex-shrink: 0;
  }
`;

/* Mobile dropdown menu removed - full nav tabs in horizontal scroll instead */
const MobileMenu = styled.div<{ $isOpen: boolean }>`
  display: none !important;
`;

const MobileNavList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const MobileMenuSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const MobileMenuActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const NavLink = styled(Link)<{ $isActive: boolean; $tier?: 'plus' | 'pro' | 'elite' }>`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: ${props => props.$tier === 'elite' ? '0.5rem 0.65rem' : '0.55rem 0.75rem'};
  font-family: ${theme.typography.fontFamily.primary};
  font-size: ${props => props.$tier === 'elite' ? theme.typography.fontSize.sm : theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${props => props.$isActive ? theme.colors.primary[600] : theme.colors.text.secondary};
  background: ${props => props.$isActive ? theme.colors.glow.mint : 'transparent'};
  border-radius: 999px;
  transition: color 0.2s ease, background 0.2s ease, transform 0.2s ease;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  position: relative;
  z-index: 0;
  margin: 0;
  overflow: visible;
  text-decoration: none;
  isolation: isolate;

  &:hover {
    background: ${theme.colors.glow.mint};
    color: ${theme.colors.primary[600]};
    transform: translateY(-2px);
  }

  svg {
    width: ${props => props.$tier === 'elite' ? '17px' : '18px'};
    height: ${props => props.$tier === 'elite' ? '17px' : '18px'};
    flex-shrink: 0;
  }

  @media (max-width: 1279px) {
    padding: ${props => props.$tier === 'elite' ? '0.45rem 0.6rem' : '0.45rem 0.65rem'};
    gap: 0.35rem;
    font-size: ${theme.typography.fontSize.sm};
    
    svg {
      width: ${props => props.$tier === 'elite' ? '16px' : '17px'};
      height: ${props => props.$tier === 'elite' ? '16px' : '17px'};
    }
  }
`;

const MobileNavLink = styled(NavLink)`
  width: 100%;
  justify-content: flex-start;
  padding: 0.85rem 1rem;
  font-size: ${theme.typography.fontSize.base};
`;

const MenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: ${theme.colors.text.primary};
  cursor: pointer;
  padding: 0.5rem;
  flex-shrink: 0;

  svg {
    width: 24px;
    height: 24px;
  }

  @media (max-width: 1023px) {
    display: flex;
    align-items: center;
  }
`;

const AuthButtons = styled.div<{ $tier?: 'plus' | 'pro' | 'elite' }>`
  display: flex;
  align-items: center;
  gap: ${props => props.$tier === 'elite' ? '1rem' : '16px'};
  flex-wrap: nowrap;
  min-width: 0;
  isolation: isolate;
  flex-shrink: 0;

  @media (max-width: 1279px) {
    gap: ${props => props.$tier === 'elite' ? '0.75rem' : '12px'};
  }

  /* Mobile: keep visible in scroll area (same as desktop) */
`;

const VIPChatButton = styled(Link)<{ $isActive: boolean; $tier?: 'plus' | 'pro' | 'elite'; $isElite?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.55rem 0.75rem;
  font-family: ${theme.typography.fontFamily.primary};
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${props => {
    if (props.$isElite) {
      return props.$isActive ? 'white' : '#92400e';
    }
    return props.$isActive ? 'white' : theme.colors.text.primary;
  }};
  background: ${props => {
    if (props.$isElite) {
      return props.$isActive 
        ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 50%, #92400e 100%)'
        : 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)';
    }
    return props.$isActive 
      ? theme.colors.primary[600]
      : theme.colors.background.primary;
  }};
  border: ${props => {
    if (props.$isElite) {
      return props.$isActive ? 'none' : '2px solid #f59e0b';
    }
    return props.$isActive 
      ? `1px solid ${theme.colors.primary[600]}`
      : `1px solid ${theme.colors.border.light}`;
  }};
  border-radius: 999px;
  transition: all 0.2s ease;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  flex-grow: 0;
  min-width: fit-content;
  text-decoration: none;
  position: relative;
  z-index: 1;
  box-shadow: ${props => {
    if (props.$isElite) {
      return props.$isActive 
        ? '0 2px 8px rgba(245, 158, 11, 0.3)'
        : 'none';
    }
    return props.$isActive 
      ? theme.shadows.md
      : 'none';
  }};
  overflow: visible;
  margin: 0;

  &:hover {
    background: ${props => props.$isElite
      ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 50%, #92400e 100%)'
      : theme.colors.primary[600]};
    color: white;
    border-color: ${props => props.$isElite ? '#f59e0b' : theme.colors.primary[600]};
    transform: translateY(-2px);
    box-shadow: ${props => props.$isElite
      ? '0 4px 12px rgba(245, 158, 11, 0.4)'
      : theme.shadows.lg};
  }

  svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }

  @media (max-width: 1279px) {
    padding: 0.45rem 0.65rem;
    gap: 0.3rem;
    font-size: ${theme.typography.fontSize.sm};
    white-space: nowrap;
    
    svg {
      width: ${props => props.$tier === 'elite' ? '16px' : '17px'};
      height: ${props => props.$tier === 'elite' ? '16px' : '17px'};
    }
  }

  /* Mobile: keep visible in scroll area (same as desktop) */
`;

const UtilityGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-left: auto;
  flex-shrink: 0;
  flex-wrap: nowrap;
  white-space: nowrap;

  @media (max-width: ${theme.breakpoints.md}) {
    flex-direction: column;
    width: 100%;
    gap: 0.5rem;
  }
`;

const UserInfo = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 1rem;
  background: ${theme.colors.glow.mint};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.primary[700]};
  min-width: 0;
  flex-shrink: 0;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;

  &:hover {
    background: ${theme.colors.mint[100]};
    box-shadow: ${theme.shadows.sm};
    transform: translateY(-1px);
  }

  @media (max-width: ${theme.breakpoints.md}) {
    width: 100%;
    justify-content: center;
  }
`;

const UserAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${theme.colors.primary[400]}, ${theme.colors.mint[400]});
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.bold};
`;

const MobileUserInfo = styled(UserInfo)`
  width: 100%;
  justify-content: flex-start;
`;

// Base navigation items (available to all tiers)
const baseNavItems = [
  { path: '/', label: 'Home', icon: Home },
  { path: '/scan', label: 'Scan', icon: Camera },
  { path: '/diy-masks', label: 'DIY Masks', icon: Leaf },
  { path: '/diet', label: 'Diet', icon: Apple },
  { path: '/food-scan', label: 'Food Scan', icon: Search },
  { path: '/progress', label: 'Progress', icon: TrendingUp },
  { path: '/results', label: 'Results', icon: Sparkles },
  { path: '/pricing', label: 'Pricing', icon: DollarSign },
];

// Elite-only navigation items (removed VIP Chat - now floating button)
const eliteOnlyNavItems: typeof baseNavItems = [];

const LatestResultPill = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0.85rem;
  border-radius: ${theme.borderRadius.full};
  background: ${theme.colors.primary[50]};
  color: ${theme.colors.primary[600]};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  text-decoration: none;
  transition: background 0.2s ease, transform 0.2s ease, color 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    background: ${theme.colors.glow.mint};
    color: ${theme.colors.primary[600]};
    transform: translateY(-1px);
  }

  @media (max-width: ${theme.breakpoints.md}) {
    display: none;
  }
`;

type NavButtonProps = React.ComponentProps<typeof Button> & { $tier?: 'plus' | 'pro' | 'elite' };

const NavActionButton: React.FC<NavButtonProps> = ({ style, $tier, ...rest }) => (
  <Button
    {...rest}
    style={{
      whiteSpace: 'nowrap',
      flexShrink: 0,
      padding: $tier === 'elite' ? '0.55rem 0.85rem' : '0.6rem 0.95rem',
      borderRadius: '999px',
      isolation: 'isolate',
      position: 'relative',
      zIndex: 0,
      fontSize: $tier === 'elite' ? theme.typography.fontSize.sm : theme.typography.fontSize.base,
      ...(style || {}),
    }}
  />
);

const DermVisitButton: React.FC<NavButtonProps> = ({ style, $tier, ...rest }) => (
  <Button
    {...rest}
    style={{
      whiteSpace: 'nowrap',
      flexShrink: 0,
      padding: $tier === 'elite' ? '0.55rem 0.85rem' : '0.6rem 0.95rem',
      borderRadius: '999px',
      isolation: 'isolate',
      position: 'relative',
      zIndex: 0,
      fontSize: $tier === 'elite' ? theme.typography.fontSize.sm : theme.typography.fontSize.base,
      background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(245, 158, 11, 0.25) 100%)',
      border: '1.5px solid #f59e0b',
      color: '#f59e0b',
      fontWeight: theme.typography.fontWeight.semibold,
      transition: 'all 0.2s ease',
      ...(style || {}),
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.background = 'linear-gradient(135deg, rgba(245, 158, 11, 0.25) 0%, rgba(245, 158, 11, 0.35) 100%)';
      e.currentTarget.style.transform = 'translateY(-1px)';
      e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = 'linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(245, 158, 11, 0.25) 100%)';
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = 'none';
    }}
    onMouseDown={(e) => {
      e.currentTarget.style.transform = 'translateY(0)';
    }}
  />
);

const ResponsiveButtonStyles = styled.div<{ $tier?: 'plus' | 'pro' | 'elite' }>`
  button, a {
    ${props => props.$tier === 'elite' ? `
      svg {
        width: 17px !important;
        height: 17px !important;
      }
    ` : ''}
    
    @media (max-width: 1279px) {
      padding: ${props => props.$tier === 'elite' ? '0.5rem 0.8rem' : '0.5rem 0.8rem'} !important;
      font-size: ${theme.typography.fontSize.sm} !important;
      
      svg {
        width: ${props => props.$tier === 'elite' ? '16px' : '17px'} !important;
        height: ${props => props.$tier === 'elite' ? '16px' : '17px'} !important;
      }
    }
  }
`;

export const Navigation: React.FC = () => {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { user, userProfile } = useAuth();
  const { checkFeature, tier } = useSubscription();

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);

  // Compose navigation items based on tier
  // Glow Bot shows for Plus, Pro, and Elite users
  // Pricing button only shows for signed-in users
  const getNavItems = () => {
    // Filter out Pricing if user is not signed in
    const filteredBaseItems = user 
      ? baseNavItems 
      : baseNavItems.filter(item => item.path !== '/pricing');
    
    if (tier === 'elite' && user) {
      // For Elite: base items + Elite-only items
      return [...filteredBaseItems, ...eliteOnlyNavItems];
    }
    // For Plus/Pro: base items only
    return filteredBaseItems;
  };

  const allNavItems = getNavItems();
  const { showToast } = useToast();
  
  // Check if we're on home page AND user is not signed in
  const isHomePageNotSignedIn = location.pathname === '/' && !user;
  
  // Check if we're on sign in/sign up page AND user is not signed in
  const isAuthPageNotSignedIn = (location.pathname === '/signin' || location.pathname === '/signup') && !user;

  const handleDermVisitClick = (e: React.MouseEvent) => {
    e.preventDefault();
    showToast(
      'Dermatology Visit feature is coming soon. Our team is working hard to bring you direct access to licensed dermatologists through GlowLens Elite.',
      'info'
    );
  };

  return (
    <Nav>
      <NavContainer $tier={tier}>
        {/* Logo for all tiers */}
        <Logo to="/" onClick={closeMenu} $tier={tier}>
          GlowLens™
        </Logo>

        <NavScrollArea $tier={tier}>
        <NavLinksContainer $tier={tier} $isHomePageNotSignedIn={isHomePageNotSignedIn} $isAuthPageNotSignedIn={isAuthPageNotSignedIn}>
          {allNavItems.map((item: typeof baseNavItems[0]) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;

                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    $isActive={isActive}
                    $tier={tier}
                    onClick={closeMenu}
                  >
                    <Icon />
                    {item.label}
                  </NavLink>
                );
              })}
          {/* Glow Bot button for Plus, Pro, and Elite - added to nav links */}
          {(tier === 'plus' || tier === 'pro' || tier === 'elite') && user && (
            <VIPChatButton
              to="/vip-chat"
              $isActive={location.pathname === '/vip-chat'}
              $tier={tier}
              onClick={closeMenu}
              $isElite={tier === 'elite'}
            >
              <Bot size={18} />
              Glow Bot
            </VIPChatButton>
          )}
        </NavLinksContainer>

        <RightGroup $tier={tier}>
          <ResponsiveButtonStyles $tier={tier}>
            <AuthButtons $tier={tier}>
            {user ? (
              <>
                {/* Derm Visit button for Elite only - placed before Profile */}
                {tier === 'elite' && (
                  <DermVisitButton size="sm" onClick={handleDermVisitClick} $tier={tier}>
                    <Stethoscope size={tier === 'elite' ? 17 : 18} />
                    Derm Visit
                  </DermVisitButton>
                )}
                  <NavActionButton variant="outline" size="sm" as={Link} to="/profile" $tier={tier}>
                    <User size={tier === 'elite' ? 17 : 18} />
                  Profile
                </NavActionButton>
              </>
            ) : (
              <>
                  <NavActionButton variant="outline" size="sm" as={Link} to="/signin" $tier={tier}>
                    <LogIn size={tier === 'elite' ? 15 : 18} />
                  Sign In
                </NavActionButton>
                  <NavActionButton size="sm" as={Link} to="/signup" $tier={tier}>
                    <UserPlus size={tier === 'elite' ? 15 : 18} />
                  Sign Up
                </NavActionButton>
              </>
            )}
          </AuthButtons>
          </ResponsiveButtonStyles>

          <MenuButton onClick={toggleMenu} aria-label="Toggle menu">
            {isMenuOpen ? <X /> : <Menu />}
          </MenuButton>
        </RightGroup>
        </NavScrollArea>
      </NavContainer>

      <MobileMenu $isOpen={isMenuOpen}>
        <MobileNavList>
          {allNavItems.map((item: typeof baseNavItems[0]) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;

              return (
                <MobileNavLink
                  key={`mobile-${item.path}`}
                  to={item.path}
                  $isActive={isActive}
                  onClick={closeMenu}
                >
                  <Icon />
                  {item.label}
                </MobileNavLink>
              );
            })}

          {/* Profile in Mobile Menu */}
          {user && (
            <MobileNavLink
              to="/profile"
              $isActive={location.pathname === '/profile'}
              onClick={closeMenu}
            >
              <User />
              Profile
            </MobileNavLink>
          )}
        </MobileNavList>

        <MobileMenuSection>
          {user ? (
            <MobileMenuActions>
              <Button
                variant="outline"
                as={Link}
                to="/profile"
                onClick={closeMenu}
              >
                <User size={16} style={{ marginRight: '0.5rem' }} />
                Profile
              </Button>
            </MobileMenuActions>
          ) : (
            <MobileMenuActions>
              <Button
                variant="outline"
                as={Link}
                to="/signin"
                onClick={closeMenu}
              >
                Sign In
              </Button>
              <Button
                as={Link}
                to="/signup"
                onClick={closeMenu}
              >
                Create Account
              </Button>
            </MobileMenuActions>
          )}
        </MobileMenuSection>
      </MobileMenu>
    </Nav>
  );
};
export default Navigation;
